import { forwardRef } from "react";
import { Loader2 } from "lucide-react";
import MascotAvatar from "./MascotAvatar";
import EmotionSticker from "./EmotionSticker";
import MessageContent from "./MessageContent";

const SUGGESTIONS = [
  "How do I apply to overseas universities?",
  "I need a career counsellor for my child.",
  "Find me a maths tutor for class 10.",
];

/**
 * Scrollable conversation list inside the widget panel.
 * Renders every chat message, the thinking loader and
 * (when the chat is fresh) a few suggestion prompts.
 */
const ChatMessageList = forwardRef(function ChatMessageList(
  { messages, loading, onSuggestionClick },
  ref,
) {
  return (
    <div
      ref={ref}
      className="flex-1 space-y-3.5 overflow-y-auto custom-scrollbar bg-gradient-to-b from-[#FBF1FF] via-white to-[#FFF1FA] px-4 py-4"
      data-testid="askpurple-messages"
    >
      {messages.map((m, i) => (
        <div key={m.id || `msg-${i}`}>
          <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={m.role === "user" ? "max-w-[85%]" : "w-full max-w-[92%]"}>
              <div
                className={`rounded-2xl px-4 py-3 text-sm leading-6 ${
                  m.role === "user"
                    ? "bg-gradient-to-br from-[#7C3AED] to-[#A855F7] text-white"
                    : "border border-purple-100 bg-white text-[#1E1E1E] shadow-sm"
                }`}
              >
                <MessageContent text={m.text} isUser={m.role === "user"} />
              </div>
            </div>
          </div>
          {m.emotion && (
            <EmotionSticker
              emotion={m.emotion}
              align={m.role === "user" ? "right" : "left"}
              size="sm"
            />
          )}
        </div>
      ))}

      {loading && (
        <div className="flex items-end justify-start gap-2">
          <MascotAvatar state="thinking" size={32} withBubble={false} variant="compact" />
          <div className="flex items-center gap-2 rounded-2xl border border-black/5 bg-white px-4 py-3 text-sm text-gray-500">
            <Loader2 size={14} className="animate-spin" /> AskPurple is thinking...
          </div>
        </div>
      )}

      {messages.length === 1 && (
        <div className="space-y-2 pt-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#C89B3C]">
            Try asking
          </p>
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => onSuggestionClick(s)}
              className="block w-full rounded-xl border border-black/5 bg-white px-3 py-2 text-left text-sm text-[#063B30] hover:border-[#C89B3C]/40"
              data-testid="askpurple-suggestion"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default ChatMessageList;
